<template>
    <div class="flex flex-col">
        <div class="mb-3 flex justify-between w-full">
            <span class="font-semibold text-md">
                {{ $t("common.dateToPosix.convertPosix") }}
            </span>
            <div class="flex items-center gap-2">
                <div v-if="!isEditingAlias" class="flex items-center gap-1">
                    <span class="font-semibold text-md">
                        {{ alias }}
                    </span>
                    <button
                        @click="startEditingAlias"
                        type="button"
                        class="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                        :aria-label="$t('common.editAlias')"
                    >
                        <pencil-icon
                            class="h-4 w-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        />
                    </button>
                </div>
                <div v-else class="flex items-center gap-1">
                    <input
                        ref="aliasInput"
                        v-model="tempAlias"
                        type="text"
                        class="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-emphasis"
                        @keyup.enter="saveAlias"
                        @keyup.escape="cancelEditingAlias"
                        @blur="saveAlias"
                    />
                    <button
                        @click="saveAlias"
                        type="button"
                        class="p-1 hover:bg-green-200 dark:hover:bg-green-700 rounded transition-colors"
                        :aria-label="$t('common.saveAlias')"
                    >
                        <check
                            class="h-4 w-4 text-green-600 dark:text-green-400"
                        />
                    </button>
                    <button
                        @click="cancelEditingAlias"
                        type="button"
                        class="p-1 hover:bg-red-200 dark:hover:bg-red-700 rounded transition-colors"
                        :aria-label="$t('common.cancelEdit')"
                    >
                        <cancel
                            class="h-4 w-4 text-red-600 dark:text-red-400"
                        />
                    </button>
                </div>
            </div>
        </div>
        <div class="flex">
            <date-select-component
                v-model:value="posix"
                enable-u-t-c
                enabled-nanoseconds
            >
                <template v-slot:button>
                    <button
                        @click="parseDate"
                        type="button"
                        class="px-4 mb-2"
                        :aria-label="$t('common.dateToPosix.convertPosix')"
                    >
                        <arrow-simple-right
                            class="h-5 w-5 text-primary-emphasis hover:text-primary-primary dark:text-primary-details"
                        />
                    </button>
                </template>
            </date-select-component>
        </div>
        <div class="flex items-center">
            <span class="w-1/2">{{ $t("common.dateToPosix.seconds") }}</span>
            <div
                v-if="resultSeconds"
                class="flex items-center w-1/2 justify-end"
            >
                <small>
                    {{ resultSeconds }}
                </small>
                <copy-clipboard-button
                    :input="resultSeconds"
                    class="fill-blue-500 ml-2"
                />
            </div>
            <span v-else>{{ $t("common.dateToPosix.loading") }}</span>
        </div>
        <div class="flex items-center">
            <span class="w-1/2">
                {{ $t("common.dateToPosix.milliseconds") }}
            </span>
            <div
                v-if="resultMilliseconds"
                class="flex items-center w-1/2 justify-end"
            >
                <small>
                    {{ resultMilliseconds }}
                </small>
                <copy-clipboard-button
                    :input="resultMilliseconds"
                    class="fill-blue-500 ml-2"
                />
            </div>
            <span v-else>{{ $t("common.dateToPosix.loading") }}</span>
        </div>
        <div class="flex items-center">
            <span class="w-1/2">
                {{ $t("common.dateToPosix.microseconds") }}
            </span>
            <div
                v-if="resultMicroseconds"
                class="flex items-center w-1/2 justify-end"
            >
                <small>
                    {{ resultMicroseconds }}
                </small>
                <copy-clipboard-button
                    :input="resultMicroseconds"
                    class="fill-blue-500 ml-2"
                />
            </div>
            <span v-else>{{ $t("common.dateToPosix.loading") }}</span>
        </div>
        <div class="flex items-center">
            <span class="w-1/2">
                {{ $t("common.dateToPosix.nanoseconds") }}
            </span>
            <div
                v-if="resultNanoseconds"
                class="flex items-center justify-end w-1/2"
            >
                <small>{{ resultNanoseconds }}</small>
                <copy-clipboard-button
                    :input="resultNanoseconds"
                    class="fill-blue-500 ml-2"
                />
            </div>
            <span v-else>{{ $t("common.dateToPosix.loading") }}</span>
        </div>
    </div>
</template>

<script lang="ts">
import type { DatesAdapter } from "inversify-utils-kit";
import { defineComponent, inject, nextTick, reactive, ref, toRefs } from "vue";

import DateSelectComponent from "@/components/dates/DateSelectComponent.vue";
import ArrowSimpleRight from "@/components/icons/ArrowSimpleRight.vue";
import Cancel from "@/components/icons/Cancel.vue";
import Check from "@/components/icons/Check.vue";
import PencilIcon from "@/components/icons/Pencil.vue";
import CopyClipboardButton from "@/components/utils/CopyClipboardButton.vue";

export default defineComponent({
    components: {
        DateSelectComponent,
        CopyClipboardButton,
        ArrowSimpleRight,
        PencilIcon,
        Check,
        Cancel
    },
    name: "DateToPosixConverter",
    props: {
        id: {
            default: () => "1",
            required: false,
            type: String
        }
    },
    setup({ id }) {
        const datesAdapter = inject("datesAdapter") as DatesAdapter;
        const aliasInput = ref<HTMLInputElement | null>(null);

        const state = reactive({
            posix: datesAdapter.now(),
            resultSeconds: "",
            resultMilliseconds: "",
            resultMicroseconds: "",
            resultNanoseconds: "",
            alias: "",
            isEditingAlias: false,
            tempAlias: ""
        });

        state.alias = `#${id}`;

        const parseDate = async () => {
            state.resultSeconds = "...";
            state.resultMilliseconds = "...";
            state.resultMicroseconds = "...";
            state.resultNanoseconds = "...";
            await new Promise((resolve) => setTimeout(resolve, 300));
            state.resultSeconds = `${Math.trunc(state.posix / 1000)}`;
            state.resultMilliseconds = `${state.posix}`;
            state.resultMicroseconds = `${state.resultMilliseconds}000`;
            state.resultNanoseconds = `${state.resultMicroseconds}000`;
        };

        const startEditingAlias = async () => {
            state.tempAlias = state.alias;
            state.isEditingAlias = true;
            await nextTick();
            aliasInput.value?.focus();
            aliasInput.value?.select();
        };

        const saveAlias = () => {
            if (state.tempAlias.trim()) {
                state.alias = state.tempAlias.trim();
            }
            state.isEditingAlias = false;
        };

        const cancelEditingAlias = () => {
            state.tempAlias = "";
            state.isEditingAlias = false;
        };

        return {
            ...toRefs(state),
            aliasInput,
            parseDate,
            startEditingAlias,
            saveAlias,
            cancelEditingAlias
        };
    }
});
</script>
